import React, { useState, useEffect } from "react";
import StudentTable from "./StudentTable";
import AddStudentModal from "./AddStudentModal";
import EditStudentModal from "./EditStudentModal";
import BulkUploadStudentsModal from "./BulkUploadStudentsModal";
import { Plus, Upload, Download } from "lucide-react";
import Papa from "papaparse";
import { toast } from "react-toastify";
import { getMergedData, updateStoredData } from "../lib/dataUtils";
import StudentProfileModal from "./StudentProfileModal";

const Students = ({}) => {
  const [students, setStudents] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [timeLogs, setTimeLogs] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = () => {
      const studentsData = getMergedData("students");
      setStudents(studentsData);

      const opportunitiesData = getMergedData("opportunities");
      setOpportunities(opportunitiesData);

      const timeLogsData = getMergedData("timeLogs");
      setTimeLogs(timeLogsData);
    };

    fetchData();
  }, []);

  const handleAddStudent = (newStudent) => {
    const updatedStudents = [...students, newStudent];
    setStudents(updatedStudents);
    updateStoredData("students", updatedStudents);
    toast.success(`${newStudent.firstName} added successfully!`);
    setIsAddModalOpen(false);
  };

  const handleEditStudent = (studentToEdit) => {
    setSelectedStudent(studentToEdit);
    setIsEditModalOpen(true);
  };

  const handleUpdateStudent = (updatedStudent) => {
    const updatedStudents = students.map((student) =>
      student.id === updatedStudent.id ? updatedStudent : student
    );
    setStudents(updatedStudents);
    updateStoredData("students", updatedStudents);
    toast.success(`${updatedStudent.firstName} updated successfully!`);
    setIsEditModalOpen(false);
    setSelectedStudent(null);
  };

  const handleDeleteStudent = (studentId) => {
    const updatedStudents = students.filter(
      (student) => student.id !== studentId
    );
    setStudents(updatedStudents);
    toast.success("Student deleted successfully!");
    updateStoredData("students", updatedStudents);
  };

  const handleExportStudents = () => {
    if (students.length === 0) {
      toast.error("No data to export.");
      return;
    }

    // Map students to include only the specified columns
    const exportData = students.map((student) => ({
      studentId: student.id,
      firstName: student.firstName,
      lastName: student.lastName,
      cohort: student.cohort,
      program: student.program,
      email: student.email,
      phone: student.phone,
      street: student.address.street,
      city: student.address.city,
      state: student.address.state,
      zip: student.address.zip,
      parentName: student.parentName,
      parentPhone: student.parentPhone,
    }));

    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    // Format the date to yyyy-mm-dd
    const date = new Date().toISOString().split("T")[0];
    link.setAttribute("href", url);
    link.setAttribute("download", `${date}-students.csv`);
    link.click();
    toast.success("Exported successfully!");
  };

  const handleBulkUpload = (uploadedStudents) => {
    const newStudents = uploadedStudents.map((student) => ({
      ...student,
      id: Date.now() + Math.random(),
    }));

    const updatedStudents = [...students, ...newStudents];
    setStudents(updatedStudents);
    updateStoredData("students", updatedStudents);

    toast.success("Students uploaded and saved successfully!");
    setIsBulkUploadModalOpen(false);
  };

  return (
    <div className="p-6 min-h-screen">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6">
        <h2 className="text-3xl font-bold text-gray-800">Students</h2>
        <div className="flex flex-col md:flex-row md:space-x-2">
          <button
            onClick={handleExportStudents}
            className="text-gray-600 hover:text-gray-800 me-3"
            aria-label="Export to CSV"
          >
            <Download size={20} />
          </button>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md flex items-center shadow-md transition duration-300"
          >
            <Plus size={20} className="mr-2" />
            Add Student
          </button>
          <button
            onClick={() => setIsBulkUploadModalOpen(true)}
            className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-md flex items-center shadow-md transition duration-300"
          >
            <Upload size={20} className="mr-2" />
            Bulk Upload
          </button>
        </div>
      </div>

      {/* Students Table */}
      <StudentTable
        students={students}
        opportunities={opportunities}
        timeLogs={timeLogs}
        onEditStudent={handleEditStudent}
        onDeleteStudent={handleDeleteStudent}
      />

      <AddStudentModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddStudent}
      />
      <EditStudentModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedStudent(null);
        }}
        student={selectedStudent}
        onSubmit={handleUpdateStudent}
      />
      {/* Bulk Upload Modal */}
      <BulkUploadStudentsModal
        isOpen={isBulkUploadModalOpen}
        onClose={() => setIsBulkUploadModalOpen(false)}
        onSubmit={handleBulkUpload}
      />

      {selectedStudent && (
        <StudentProfileModal
          student={selectedStudent}
          onClose={() => setSelectedStudent(null)}
          opportunities={opportunities}
          timeLogs={timeLogs}
        />
      )}
    </div>
  );
};

export default Students;
